// Adapted from pagila-rails-portal/app/javascript/coreui/base_component.js
import { dataConfig, dispatch } from "./dom"

// Component objects are deliberately small and DOM-scoped. Stimulus owns their
// lifetime; this WeakMap only prevents duplicate enhancement when Turbo inserts
// nested fragments or a MutationObserver sees markup generated by a component.
export default class BaseComponent {
  static instanceStore() {
    if (!Object.prototype.hasOwnProperty.call(this, "instances")) this.instances = new WeakMap()
    return this.instances
  }

  static getInstance(element) {
    return this.instanceStore().get(element) || null
  }

  static getOrCreateInstance(element, config = {}) {
    return this.getInstance(element) || new this(element, config)
  }

  constructor(element, config = {}) {
    this.constructor.getInstance(element)?.dispose()
    this.element = element
    this.config = { ...this.constructor.defaults, ...dataConfig(element), ...config }
    this.abortController = new AbortController()
    this.originalHTML = element.innerHTML
    this.originalClassName = element.className
    this.originalAttributes = new Map([...element.attributes].map(({ name, value }) => [name, value]))
    this.generated = []
    this.frames = new Set()
    this.constructor.instanceStore().set(element, this)
  }

  get signal() {
    return this.abortController.signal
  }

  listen(element, type, listener, options = {}) {
    element.addEventListener(type, listener, { ...options, signal: this.signal })
  }

  emit(name, detail = {}, options = {}) {
    return dispatch(this.element, `${name}.coreui.${this.constructor.componentName}`, detail, options)
  }

  restoreAttribute(name) {
    if (this.originalAttributes.has(name)) this.element.setAttribute(name, this.originalAttributes.get(name))
    else this.element.removeAttribute(name)
  }

  nextFrame(callback) {
    const schedule = (fn) => {
      const id = requestAnimationFrame(() => {
        this.frames.delete(id)
        if (!this.signal.aborted) fn()
      })
      this.frames.add(id)
    }
    schedule(() => schedule(callback))
  }

  afterTransition(element, callback, animated = true) {
    const style = getComputedStyle(element)
    const seconds = (value) => value.split(",").map((part) => parseFloat(part) || 0)
    const durations = seconds(style.transitionDuration)
    const delays = seconds(style.transitionDelay)
    const duration =
      animated && !matchMedia("(prefers-reduced-motion: reduce)").matches
        ? Math.max(...durations.map((value, index) => value + delays[index % delays.length])) * 1000
        : 0
    if (!duration) return callback()
    let complete = false
    const finish = (event) => {
      if (event && event.target !== element) return
      if (complete || this.signal.aborted) return
      complete = true
      clearTimeout(timer)
      element.removeEventListener("transitionend", finish)
      this.signal.removeEventListener("abort", cancel)
      callback()
    }
    const timer = setTimeout(() => finish(), duration + 5)
    const cancel = () => clearTimeout(timer)
    this.listen(element, "transitionend", finish)
    this.signal.addEventListener("abort", cancel, { once: true })
  }

  // Persistent state is serialized by the Stimulus owner before Turbo clones HTML.
  snapshotState() {
    return {}
  }

  restoreState() {}

  prepareForCache() {
    const state = this.snapshotState()
    this.element.querySelectorAll("input, textarea, option").forEach((field) => {
      if (field.tagName === "OPTION") field.defaultSelected = field.selected
      else if (field.tagName === "TEXTAREA") field.defaultValue = field.value
      else {
        if (field.type !== "file") field.setAttribute("value", field.value)
        field.defaultChecked = field.checked
      }
    })
    this.dispose()
    return state
  }

  dispose() {
    if (this.signal.aborted) return
    this.abortController.abort()
    this.frames.forEach((id) => cancelAnimationFrame(id))
    this.frames.clear()
    this.generated.forEach((element) => element.remove())
    if (this.constructor.getInstance(this.element) === this) this.constructor.instanceStore().delete(this.element)
  }
}
